import {
  assertEvent,
  assign,
  fromPromise,
  raise,
  setup,
  type SnapshotFrom,
} from 'xstate';

import {
  createEvent,
  getAvailability,
  getAvailabilityRange,
  noAvailability,
  updateEvent,
} from '../../services/schedularService';
import type {IGetAvailabilityQuery} from '../../types/dto/IGetAvailabilityQuery';

import type {
  IBookingMachineContext,
  IBookingMachineEvents,
  IBookingMachineInput,
  IBookingMachineOutput,
  SequenceDay,
} from './bookingMachine.types';
import {
  canNavigateNextWeek,
  updateSequenceDays,
  getSelectedStartDateOfWeekFromSequenceDays,
  getSelectedTimeslots,
  generateSequenceDaysFromDayZeroDateChange,
  generateSequenceDaysFromAnyDayDateChange,
} from '../../utils/booking';
import type {IAdditionalQuestionAnswer} from '../../types/dto/IAdditionalQuestionAnswer';
import {
  addDays,
  formatISO,
  addTZDays,
  subTZDays,
  startOfTZDay,
  createTZDate,
} from '../../utils/date';
import type {AxiosError} from 'axios';
import {captureSentryMessage} from 'src/utils/sentry';
import type {IEvent} from '../../types/IEvent';


export const bookingMachine = setup({
  types: {
    context: {} as IBookingMachineContext,
    events: {} as IBookingMachineEvents,
    input: {} as IBookingMachineInput,
    output: {} as IBookingMachineOutput,
  },
  actors: {
    getAvailabilityRange: fromPromise(
      async ({
        input,
      }: {
        input: {templateId: string; eventId?: string; sessionId?: string};
      }) =>
        getAvailabilityRange(input.templateId, {
          event_id: input.eventId,
          session_id: input.sessionId,
        }),
    ),
    getAvailability: fromPromise(
      async ({
        input,
      }: {
        input: {
          templateId: string;
          timezone: string;
          eventId?: string;
          sourceId?: string;
          startDate: string;
          endDate: string;
          sequenceDay?: number;
          sequencePrevDt?: string;
        };
      }) => {
        const params: IGetAvailabilityQuery = {
          viewer_timezone: input.timezone,
          event_id: input.eventId,
          source_id: input.sourceId,
          start_dt: input.startDate,
          end_dt: input.endDate,
          ...(input.sequenceDay && {
            sequence_day: input.sequenceDay,
          }),
          ...(input.sequencePrevDt && {sequence_prev_dt: input.sequencePrevDt}),
        };

        return getAvailability(input.templateId, params);
      },
    ),
    updateEvent: fromPromise(
      async ({
        input,
      }: {
        input: {
          eventId: string;
          date: string;
          startTime: string;
          timezone: string;
          attendee: string;
          firstAvailableDatetime: string;
          reason: string;
          aid: string;
        };
      }) =>
        updateEvent(input.eventId, {
          date: input.date,
          start_time: input.startTime,
          viewer_timezone: input.timezone,
          attendee: input.attendee,
          first_available_datetime: input.firstAvailableDatetime,
          reason: input.reason,
          aid: input.aid,
        }),
    ),
    sendNotification: fromPromise(
      ({
        input,
      }: {
        input: {
          templateId: string;
          guestName: string;
          guestEmail: string;
          guestPhone: string;
          sessionId?: string;
          guestTimezone: string;
          sequenceDay: number;
        };
      }) =>
        noAvailability(input.templateId, {
          sequence_day: input.sequenceDay,
          guest_name: input.guestName,
          guest_email: input.guestEmail,
          guest_phone: input.guestPhone,
          session_id: input.sessionId,
          guest_timezone: input.guestTimezone,
        }),
    ),
    createEvent: fromPromise(
      async ({
        input,
      }: {
        input: {
          templateId: string;
          timezone: string;
          date: string;
          startTime: string;
          guestName: string;
          guestEmail: string;
          guestPhone: string;
          firstAvailableDatetime: string;
          sessionId?: string;
          language: string;
          additionalQuestionAnswers: IAdditionalQuestionAnswer[];
          selectedTimeslots: Array<{
            day: number;
            date: string;
            start_time: string;
          }>;
        };
      }) => {
        return createEvent({
          template_id: input.templateId,
          viewer_timezone: input.timezone,
          date: input.date,
          start_time: input.startTime,
          guest_name: input.guestName,
          guest_email: input.guestEmail,
          guest_phone: input.guestPhone,
          first_available_datetime: input.firstAvailableDatetime,
          additional_question_answers: input.additionalQuestionAnswers,
          session_id: input.sessionId,
          guest_language: input.language,
          selected_timeslots: input.selectedTimeslots,
        });
      },
    ),
  },
  guards: {
    isTimeSlotUnavailable: ({context}) => {
      const error = context.error as AxiosError<{errors: string[]}>;
      const errorKey = error?.response?.data?.errors?.[0];
      const unavailableTimeSlotErrorKeys = [
        'UnavailableTimeSlot',
        'TimeSlotNotAvailable',
      ] as const;
      return unavailableTimeSlotErrorKeys.includes(
        errorKey as typeof unavailableTimeSlotErrorKeys[number],
      );
    },
    isSlotsAvailableForCurrentWeek: ({context}) => {
      const {sequenceDays, currentSequenceNumber, availabilityRange} = context;

      if (!availabilityRange) {
        throw new Error('Availability range is not found');
      }

      const sequenceNumber = currentSequenceNumber ?? 0;
      const selectedDay = sequenceDays?.find(
        (day) => day.sequenceNumber === sequenceNumber,
      );
      const selectedStartDateOfWeek = selectedDay?.selectedStartDateOfWeek;
      const timeSlots = selectedDay?.timeSlots;

      if (!selectedStartDateOfWeek) {
        throw new Error(
          'Selected start date of week is not found for current day',
        );
      }

      if (!timeSlots) {
        return false;
      }

      const weekStart = formatISO(selectedStartDateOfWeek, {
        representation: 'date',
      });
      const weekEnd = formatISO(addDays(selectedStartDateOfWeek, 6), {
        representation: 'date',
      });

      const hasAvailableSlotsInCurrentWeek = Object.keys(
        timeSlots.free_time_slots || {},
      ).some((dateKey) => {
        return dateKey >= weekStart && dateKey <= weekEnd;
      });

      return hasAvailableSlotsInCurrentWeek;
    },
    isNewBooking: ({context}) => !context.eventDetails,
    isNoSlotsAvailableTillLastWeek: ({context}) => {
      const {sequenceDays, currentSequenceNumber, availabilityRange, timezone} =
        context;

      if (!availabilityRange) {
        throw new Error('Availability range is not found');
      }
      const selectedStartDateOfWeek =
        getSelectedStartDateOfWeekFromSequenceDays(
          sequenceDays,
          currentSequenceNumber ?? 0,
        );

      if (!selectedStartDateOfWeek) {
        throw new Error(
          'Selected start date of week is not found for current day',
        );
      }

      const currentSequenceDay = sequenceDays?.find(
        (day) => day.sequenceNumber === currentSequenceNumber,
      );
      const timeSlots = currentSequenceDay?.timeSlots;
      const isSlotsNotAvailable =
        Object.keys(timeSlots?.free_time_slots || {}).length > 0;

      const canNavigateToNextWeek = canNavigateNextWeek(
        timezone,
        selectedStartDateOfWeek,
        availabilityRange,
        Boolean(isSlotsNotAvailable ?? false),
        currentSequenceNumber,
      );

      return Boolean(!isSlotsNotAvailable) && !canNavigateToNextWeek;
    },
    isDayZero: ({context}) => {
      return (context.currentSequenceNumber ?? 0) === 0;
    },
    isRescheduleOrCancellationNotAllowed: ({
      context,
    }: {
      context: IBookingMachineContext;
    }) => {
      return (
        Array.isArray(context.eventDetails) &&
        context.eventTemplate?.distribution_rule === 'sequential_interview'
      );
    },
  },
}).createMachine({
  /** @xstate-layout N4IgpgJg5mDOIC5QCMD2qDWBLAdlAxADYCGeArsTAHRkAOExALmANoAMAuoqLarFoyyoc3EAA9EAWgCMAFgBsAdioAmAJwBWDfLZaAzPJUaAHABoQAT0TTpKvVQ1sVbWWtnTF0x9IC+P82iYuFBUAMYAFmChGABKcBGQZIRgAPIATgDCpKFghCSCwgByqIwAgnmoAO6Q+OxcSCC8-AUiDRIIkoqK8lTGakpsxsYKsnom5lYIxvL2anYzzrZ6xrZ+AejYeGGR0XGwCRBJqZnZuflCOMVlFdUQtdL1PHwCF6LtkstsvRoqznpecjYOnkE0Q01m8z0izsKzWIECmxCxAAbsQsCRkOiBBYYqQYAAZVDECDBfAQYRgKi4ZGYSkI4JUFFojFYxg4vFgQnE4IIamoUJMC51OqiJovYRvKSLKh6XTGDRqRRA+ZmSyIfo9NTGTxsDzTExauH0rZM9HETGEbG4vCcokkvD4MBpNKoNJUWj5ABmroAtlRjUjUWaLVaOVz7VBeTgaQKWsLOKLni1JQgVLIVL0dLq2Gw9LKTIo9KCEJoM25dWnZYWVPJfP54RsGQxmAAVLA+sAABSw0Sd+FgjGIaUYABEmKlPQB1MBgDDbDkihpi5NtaxfDQ2ZZeJXy4xOYs2OZUUbGMYbn5atR6I2NrbNsBtjvd3tpMnj+c2xdPZqvVcdKH2DWbD9J4sqKl4GgHnM9hAho-x6JoihqDm171gGVD3o+XY9hgfbVLOVA4GAYiMF+jRJr+oDvEMXw1nBwFOLYrgqAeKheMesiyI4hhKkq8jyDeQR3uOWHPrhr74XOtBpGAyJkculHiFINEysh-wmKe3gsWqCDSAYsjHooKjTEo8haLI0yCYiGEie22Evm+FiEcRpEJkuFESn+Mi1jKCHGfIe4KshiiseoVCDN4RmyEqchWU2tlPjhfYME50myfJHmtFRUgbtIVBqSoijRa4XR6CFOl6dIaiqNFtYVlV6ayHFwmtnZYlOoyQYspabLhqS5JEVS0a0v6t4hJhbVJW6prddifV4FGMaCsI8aPORP6edlUz2CY0hKBoijaHIemqpMe1lVQXRMRuiy6gJaFjTZrWJS+nXMuarIWPNBBOi6boekw3ppH66ETS94lvcGn3fYt-LLTgq2JhtWVKQg3QZlCahY3p3S1tFB6yIM4XGTm0zGcMXjNeNCX2RDM0fT1X12jUGXIymYz5U4OaKCsgxjNqUEWeF7iaECLjyoVVNPQ+k2vfTIa9czdwsA8SPiij7QmGWxjOLWUIBWV5VnW4xjC9I8pmQBgxqFLYO0x18vQ0rtQqGtCmbaj-FfMBcijP0ui-CCOmLHlWhmRTTiXoots0+101dQz2IAKLOq6+AyYwaQWKz6vs-I1XyITwFlXoFnk7IBNhUYXFqHIRlXoYMfPfb8fvQrFgAMqECUsD4IIHawN3jBhMInpYMDOcrltfT5dXWg5l4AUWcWbH5zK+fm8hZdNQ9QnU83cdUP3cBDxko-jz68N93Zg8lCPOBjxPbnfrnf5VcoXF7i4e246TK9cR-VeOYtR7mGE3GW4MOrH1vowM+D8L5XwDAAMV9FQKAqBJ6KXeM4BwhcDB6RuiA+UK9tDKD2sqfaRUsY73WHvaWokppHxvqfc+wMr4yX2JEQ4yQ4jEFgMIFBwM0EYOfutV+W1ljVSVEYIqtggSeFOogIwPlph6X6O4BCfRwEMNetAlh8C2EtHwMgYg0RMEe3eB4cK+1NLajkXIFelYZQ1lPIqH2hN7q0OsnbQ+jtGZXCwJ6Cw+AcAlECRYAAEnwYe6DzEa2sKXDQ+UCyFgstqJQUEdC4KMlCQmxkbA0IbHQnxjC-HYgCUExy7oZJyVEe7FGxsNAGV0EZeUspRhsVVO0PSdgZQHQOkhNMZljJSzKWya0MAU5-XTmATO2c6mZRTHIqgXhXB9F+HmWU2lJjyjyhTMqPx+JKDrF4hkoTBCVJMWYhZbMGiNJ6D8LwvxNwuAVOYdoQzTa7kOkYOwzhFRS3OeEqgQMfQhLCUEqgsAyDIB9AIOJSyiqm3cBZBQ2g8GHRXnIew2pNDaAlh4VCpythAshdC2FAhBAOgGpSPkuFRp0NJU5clcLGBUsjHyWMQpOAIr-GxJU1j66Kn5jzCuwdS49HcAU1Zgx36AohcymFrL2WOlTv9L0qD0JMqhUqylPJOXw0Ru5W5W0qpY3yodCyjgtBzEcQYS6akTCcS5p4op1ltW-TTtqll8KbniNRosZQYwnCfB1k0-OWLRi9CQlxOQ-kDBS2Qb6YxY1BEg1MRgXlW0PhcRlAoZC-EKY2EUamDcPQ7Dpi6FqQ6XRE2ptQaC-AoKdUUtcm7RZXl+IZkLgFHtio5ijBXn0ct4FVl2A3ECOte800tuVf1CkQ0aT0vQkmoRPq2X6uGlylaPK-VT1RpIBUXwVg2DkCsAK-RtmIFLrofKtg4LuFxU4KdiIZ3rpVZ69VgNNWPVXX6d9m6lpxl3e2k1B6oRfBmLzXKRkgTFn+ECKghhYLm0cFjeUL7ggzs-bUPdWCpR5Wit0QmoxzzbhXv0PKwJ+LSDYKVWsmG8DYbVfcUD-rsE5l6JxPoOtDYLCDpMdQPlqN1Xoyct1DI-1UAzlnDuurGBNtQeurNB6mmAU8OoLGsHOJqAoyYJDSF+K5TGAFRjUAZ1kBwPLZIWEu4lBmZ6WZERSgJ3bsIlTli17uHrjWUy6YS1GD2qoUuUJeO6F1IU9CHCDhHDTTMzhiQeFgD4QIzVGaPOIANis2q-xDDGVrrp4OB11PLF+MMQqCEpbRa4bF1B1XEtgF4fwnAcX6vcMa8l5rNB6Djgy1MAKl1cXaBDT8SCwcdZ5WMu4Oj2obpVfiDV5IM62tHCa6l4G18B5D3vo-H0fWmndvcPnTSShuhGyUZoHoZUApwVPEqZY82EvteWwthraa5OtpVTSxdI0ouvee3V-7tXgYfbnQtA1wGOD7ftWmArJhbv-BLaF02IFnAjG6A93e1kVtLcB094HPpQd6odJ+90GqhF-fx7joROOwDvfk+y2G26EYgbVvu9oe4Mx0ZySVC2CF4NAjypeGiNYLJ60ezF6nfocN9bjfYWRqykIeDo1e1MmhTaGH4nMQZ6KVAS8W3T1BMvVbGvY9YfJl1-J7jkP0PMqv1Aw6Lp0pUyFND67e6gyz1mIFgDs4wQRexJdgAc058ILm26fQD0D5I7m8MWISd0b4p48tDE4kWcVnGaNlUajoG2WOGS05e3MwPBv6etpmcX6PhuQcM+CH12Dl0dNIX+HIMOFHPDhV0Gn7oP9C5+HrKEiAcBRABjZ-hjothDqN4lTMfOOhVeHWRSe+Uuo9o2A0FLBIuwq-pCyDgHIeR4ZXHKN3W4Y-48T9+MoIqM+DD+1V6ogzBgF4HRmJV-PWxad74P+cIoJQT9VCQDn7xIdCHR5RqKBzyLDBjaTC24rKFSFxzCDC8x64f6BgR6MwTK2jch4DAEpiSDOAfwGDLDnpOCeAzDFjQH5QnoWR7TpKRaPQlIvh4FeQ2qcxQhFRUKlTna6S0YGRXjDClZahVT8TaKyx0yuZOw4FQAsFbQCpAg6Ciqi6uBXgHhlQZhcSlyKinhlRgJoH0LiEOySGMzhhAGm7s5ghJLyi5SFxdA8w8YHhITIpeyVoIH+ZiGQKtxQyMxTKuiyGow-AGTqFBQbIbhjCVzVRzy1zRQO6Nz6FMESEYHYh+7wDmHj60Z7IXiGDmQ6wrA8HOAHQrLmz5JsQ9KlweEtyQyzRshJw+i0Bsj+HtCFxfCHQKiFiKjpiGArzASmwu5sRkHARuAMHFKxyMJ6IlBwK7bwyNGIBFRIa2D9AHQ2BOE8wrxaH5Q6CjaLw-A1gVG+LGHlIKozG6QuDVSxocEDrDIlpVRNLhQHTGT0QdLmyjIHHjIci+FpDHFGDew8xK5dCz5ISUEbhcY4yHL8SeBmZyahA5CwCpEvwWG6TyjfBZhdA1xKD4w6R5hOD5SyhmSyqlGcTyoXKdxkDQlwBwliIIlsRajBZoaDCFzaA1j-wzCcxky5E9J2BEnhLHF7Q+QzYxpdBDBDBQTaAOBAKjZhpQhcmQqgo8k8yEY1hgHPK7QwFKIuDNJsQ7EvI1zSmKqfbBBynAThS-FuCClCmOLuB3rhwzAzZ57EohAepqpfF2B5TBrlghr-L5HYrRpuAbj+az5mZprHGdCygOD4pZiuDpjqArz1zHh8yLzmzkGBkNq+jBlv5in3G0aUZ25DpBbITDCFjEFbjRz6FSYAa4FpEX6SABQZhhzTA5iRm-CFaTBViEb-Fah+Skwln2kMqvpG5OmVkgGHqmxGA0RLF9AAjaDwbLG9DmzJ4LyGzJk06zKybybBklyqAKjjoO5lQNR6bVQmYBRyJFxKBLl+he6uY2Z2R+7BnIouJcSFSwQuCYrjZ7jHiyheBDC1z3pEoSaf5V5BmDkpjRRfDpgbi1xkxpicSqmpg5KZgKiOB3YrDDHY4AV45B5rYtaplAV-jgWqA0bK6yhngBYnrrwmCmRzCKHu4A7LlU7V6E614Vnwnj4bh3ngXmx5acRiotm6BSJ7RcSEyOAVrUUE5UCfo8mDBkJzByBKCFRMTp48WniqD8WC5lZVQiVS40BWaXk+5+5R50U8mlZIb-A8xQVy5DoczaBQhTbTCBQaX0XSYrk4hoU16toSW-CqB5Edn6yFwBaXrUF6hbwlH94+BAA */
  context: ({input}) => input,
  id: 'booking',
  initial: 'checkRescheduleOrCancellationNotAllowed',
  on: {
    'language.update': {
      actions: assign({
        language: ({event}) => {
          return event.language;
        },
      }),
    },
  },
  states: {
    checkRescheduleOrCancellationNotAllowed: {
      always: [
        {
          target: 'rescheduleCancellationNotAllowed',
          guard: 'isRescheduleOrCancellationNotAllowed',
        },
        {
          target: 'availabilityRangeLoading',
        },
      ],
    },
    rescheduleCancellationNotAllowed: {},
    availabilityRangeLoading: {
      invoke: {
        id: 'booking.availabilityRangeLoading:invocation[0]',
        input: ({context: {eventDetails, session, eventTemplate}}) => {
          const templateId =
            eventTemplate?.id ||
            (eventDetails as IEvent)?.scheduled_from_template_id;
          if (templateId) {
            return {
              templateId,
              eventId: (eventDetails as IEvent)?.id,
              sessionId: session?.id,
            };
          }
          throw new Error('Template id not found');
        },
        onDone: {
          target: 'dateTimePicker',
          actions: assign({
            availabilityRange: ({event}) => event.output,
            currentSequenceNumber: 0,
            sequenceDays: ({event, context}) => {
              const startAt = event.output.start_at;
              if (!startAt || isNaN(startAt)) {
                captureSentryMessage(
                  'Invalid start_at from Availability Range API',
                  {
                    level: 'error',
                    tags: {
                      product: 'scheduler',
                    },
                    extra: {
                      apiParams: {
                        templateId: context.eventTemplate,
                        sessionId: context.session?.id,
                        eventId: (context.eventDetails as IEvent)?.id,
                      },
                      timezone: context.timezone,
                      rangeApiResponse: event.output,
                      eventDetails: context.eventDetails,
                      eventTemplate: context.eventTemplate,
                    },
                  },
                );
              }
              const startDayOfWeekFromRangeApi = createTZDate(
                startAt * 1000,
                context.timezone,
              );
              return [
                {
                  sequenceNumber: 0,
                  selectedStartDateOfWeek: startDayOfWeekFromRangeApi,
                  selectedDate: undefined,
                  selectedTimeSlot: undefined,
                },
              ];
            },
          }),
        },
        onError: {
          target: 'availabilityRangeError',
          actions: assign({
            error: ({event}) => event.error,
          }),
        },
        src: 'getAvailabilityRange',
      },
    },

    dateTimePicker: {
      initial: 'availabilityLoading',
      on: {
        'startDateOfWeek.change': {
          target: '#booking.dateTimePicker.availabilityLoading',
          actions: assign({
            sequenceDays: ({context, event}): SequenceDay[] => {
              // Preserve existing timeslots when changing week for sequence 0
              const existingDayZero = context.sequenceDays?.[0];
              return [
                {
                  sequenceNumber: 0,
                  selectedStartDateOfWeek: event.date,
                  selectedDate: existingDayZero?.selectedDate,
                  selectedTimeSlot: undefined,
                  timeSlots: existingDayZero?.timeSlots,
                },
              ];
            },
          }),
        },
        'date.change': {
          actions: assign({
            sequenceDays: ({context, event}): SequenceDay[] => {
              const {
                sequenceDays = [],
                eventTemplate,
                timezone,
                currentSequenceNumber = 0,
              } = context;
              const changedDaySequenceNumber = currentSequenceNumber;

              const generatedSequenceDays =
                generateSequenceDaysFromAnyDayDateChange({
                  changedDaySequenceNumber,
                  newDate: event.date,
                  existingSequenceDays: sequenceDays,
                  sequence: eventTemplate?.metadata?.sequence,
                  timezone,
                });

              return generatedSequenceDays;
            },
          }),
          target: 'dateTimePicker.availabilitySlots',
        },
        'week.next': {
          guard: 'isDayZero',
          actions: raise(
            ({context: {sequenceDays, currentSequenceNumber, timezone}}) => {
              const sequenceNumber = currentSequenceNumber ?? 0;
              const selectedStartDateOfWeek =
                getSelectedStartDateOfWeekFromSequenceDays(
                  sequenceDays,
                  sequenceNumber,
                );
              if (!selectedStartDateOfWeek) {
                throw new Error('Selected start date of week is not found');
              }
              return {
                type: 'startDateOfWeek.change',
                date: addTZDays(selectedStartDateOfWeek, 7, timezone),
              };
            },
          ),
        },
        'week.prev': {
          guard: 'isDayZero',
          actions: raise(
            ({context: {sequenceDays, currentSequenceNumber, timezone}}) => {
              const sequenceNumber = currentSequenceNumber ?? 0;
              const selectedStartDateOfWeek =
                getSelectedStartDateOfWeekFromSequenceDays(
                  sequenceDays,
                  sequenceNumber,
                );
              if (!selectedStartDateOfWeek) {
                throw new Error('Selected start date of week is not found');
              }
              return {
                type: 'startDateOfWeek.change',
                date: subTZDays(selectedStartDateOfWeek, 7, timezone),
              };
            },
          ),
        },
        'day.next': {
          target: '#booking.dateTimePicker.availabilityLoading',
          actions: assign({
            currentSequenceNumber: ({context}) =>
              (context.currentSequenceNumber ?? 0) + 1,
          }),
        },
        'day.prev': {
          target: '#booking.dateTimePicker.availabilityLoading',
          actions: assign({
            currentSequenceNumber: ({context}) =>
              (context.currentSequenceNumber ?? 0) - 1,
          }),
        },
      },
      states: {
        availabilityLoading: {
          invoke: {
            input: ({
              context: {
                eventDetails,
                eventTemplate,
                timezone,
                session,
                availabilityRange,
                sequenceDays,
                currentSequenceNumber,
              },
            }) => {
              const templateId =
                eventTemplate?.id ||
                (eventDetails as IEvent)?.scheduled_from_template_id;
              try {
                if (!templateId || !availabilityRange) {
                  throw new Error('Template id or availabilityRange not found');
                }

                const sequenceNumber = currentSequenceNumber ?? 0;
                const selectedStartDateOfWeek =
                  getSelectedStartDateOfWeekFromSequenceDays(
                    sequenceDays,
                    sequenceNumber,
                  );

                if (!selectedStartDateOfWeek) {
                  throw new Error(
                    'Could not determine selectedStartDateOfWeek for current day',
                  );
                }

                const currentDate = selectedStartDateOfWeek;
                const addedDate = addTZDays(currentDate, 7, timezone);
                const startDate = formatISO(
                  startOfTZDay(selectedStartDateOfWeek, timezone),
                  {
                    representation: 'complete',
                  },
                );
                const endDate = formatISO(startOfTZDay(addedDate, timezone), {
                  representation: 'complete',
                });

                // Only include sequence_day and sequence_prev_dt if sequenceNumber is not 0
                let sequenceDay: number | undefined;
                let sequencePrevDt: string | undefined;

                if (sequenceNumber !== 0) {
                  sequenceDay = sequenceNumber;
                  const prevSequenceNumber = sequenceNumber - 1;
                  const prevDay = sequenceDays?.find(
                    (day) => day.sequenceNumber === prevSequenceNumber,
                  );
                  const startTimeOfPrevDay =
                    prevDay?.selectedTimeSlot?.start_time;

                  // sequencePrevDt is the start time of the previous day with timezone
                  sequencePrevDt =
                    prevDay?.selectedDate && startTimeOfPrevDay
                      ? formatISO(
                          createTZDate(
                            `${prevDay.selectedDate}T${startTimeOfPrevDay}:00`,
                            timezone,
                          ),
                          {
                            representation: 'complete',
                          },
                        )
                      : undefined;
                }

                return {
                  templateId,
                  timezone,
                  eventId: (eventDetails as IEvent)?.id,
                  sourceId: session?.source_id,
                  startDate,
                  endDate,
                  sequenceDay,
                  sequencePrevDt,
                };
              } catch (error) {
                captureSentryMessage('Error setting availability api params', {
                  level: 'error',
                  tags: {product: 'scheduler'},
                  extra: {
                    error,
                    timezone,
                    eventDetails,
                    eventTemplate,
                    session,
                    availabilityRange,
                    sequenceDays,
                    currentSequenceNumber,
                  },
                });
                throw error;
              }
            },
            onDone: {
              target: 'availabilityLoaded',
              actions: assign({
                sequenceDays: ({context, event}) => {
                  const {
                    sequenceDays,
                    currentSequenceNumber,
                    eventTemplate,
                    timezone,
                  } = context;
                  const firstAvailableDate = Object.keys(
                    event.output?.free_time_slots || {},
                  )[0];
                  const sequenceNumber = currentSequenceNumber ?? 0;

                  if (sequenceNumber === 0) {
                    const dayZeroSequenceDay = {
                      ...(sequenceDays?.[0] ?? {}),
                      selectedDate: firstAvailableDate,
                      timeSlots: {
                        free_time_slots: {
                          ...sequenceDays?.[0].timeSlots?.free_time_slots,
                          ...event.output?.free_time_slots,
                        },
                      },
                    };
                    return generateSequenceDaysFromDayZeroDateChange({
                      dayZeroSequenceDay: dayZeroSequenceDay as SequenceDay,
                      sequence: eventTemplate?.metadata?.sequence,
                      timezone,
                    });
                  }
                  // else update the selected date and time slot for the current day
                  return updateSequenceDays(sequenceDays, sequenceNumber, {
                    ...(sequenceDays?.[sequenceNumber] ?? {}),
                    selectedDate: firstAvailableDate,
                    timeSlots: event.output,
                  }) as SequenceDay[];
                },
              }),
            },
            onError: {
              target: 'availabilityError',
              actions: assign({
                error: ({event}) => event.error,
              }),
            },
            src: 'getAvailability',
          },
        },

        availabilityLoaded: {
          always: [
            {
              target: 'availabilitySlots',
              guard: {
                type: 'isSlotsAvailableForCurrentWeek',
              },
            },
            {
              target: 'availabilityNotify',
              reenter: true,
              guard: 'isNoSlotsAvailableTillLastWeek',
            },
            {
              target: 'availabilityEmpty',
              reenter: true,
            },
          ],
        },

        availabilityError: {
          on: {
            retry: {
              target: 'availabilityLoading',
            },
          },
        },

        availabilitySlots: {
          on: {
            'timeslot.confirm': {
              target: 'timeslotConfirmation',
              actions: assign({
                sequenceDays: ({context, event}): SequenceDay[] =>
                  updateSequenceDays(
                    context.sequenceDays,
                    context.currentSequenceNumber ?? 0,
                    {
                      selectedTimeSlot: event.timeSlot,
                    },
                  ) as SequenceDay[],
              }),
            },
          },
        },

        availabilityEmpty: {},

        timeslotConfirmation: {
          on: {
            'timeslot.confirm': {
              target: 'timeslotConfirmation',
              actions: assign({
                sequenceDays: ({context, event}): SequenceDay[] =>
                  updateSequenceDays(
                    context.sequenceDays,
                    context.currentSequenceNumber ?? 0,
                    {
                      selectedTimeSlot: event.timeSlot,
                    },
                  ) as SequenceDay[],
              }),
            },
            'bookingForm.go': {
              target: '#booking.bookingForm',
              guard: 'isNewBooking',
            },
            'rescheduleReasonForm.go': {
              target: '#booking.rescheduleForm',
            },
            back: {
              target: 'availabilitySlots',
              actions: assign({
                sequenceDays: ({context}): SequenceDay[] => {
                  // Always clear Day 1's time slot (sequenceNumber 0)
                  return updateSequenceDays(context.sequenceDays, 0, {
                    selectedTimeSlot: undefined,
                  }) as SequenceDay[];
                },
                // If on Day 2 or later, go back to Day 1 (sequenceNumber 0)
                currentSequenceNumber: ({context}) => {
                  const sequenceNumber = context.currentSequenceNumber ?? 0;
                  return sequenceNumber > 0 ? 0 : sequenceNumber;
                },
              }),
            },
          },
        },

        availabilityNotify: {
          on: {
            'notifyHost.go': {
              target: '#booking.notify',
              reenter: true,
            },
            'day.prev': {
              target: 'availabilityLoading',
              actions: assign({
                currentSequenceNumber: ({context}) =>
                  (context.currentSequenceNumber ?? 0) - 1,
              }),
            },
          },
        },
      },
    },

    availabilityRangeError: {
      on: {
        retry: {
          target: 'availabilityRangeLoading',
        },
      },
    },

    bookingSuccess: {
      type: 'final',
      entry: assign({
        finalState: 'BookingSuccess',
      }),
    },

    notifySuccess: {
      type: 'final',
      entry: assign({
        finalState: 'NotifySuccess',
      }),
    },

    notify: {
      initial: 'form',
      on: {
        back: {
          target: '#booking.dateTimePicker.availabilityLoading',
          actions: assign({
            currentSequenceNumber: ({context}) =>
              (context.currentSequenceNumber ?? 0) - 1,
          }),
        },
      },
      states: {
        form: {
          on: {
            'notify.submit': {
              target: 'submitting',
            },
          },
        },

        submitting: {
          invoke: {
            input: ({
              context: {
                session,
                eventTemplate,
                eventDetails,
                timezone,
                currentSequenceNumber,
              },
              event,
            }) => {
              assertEvent(event, 'notify.submit');
              const templateId =
                eventTemplate?.id ||
                (eventDetails as IEvent)?.scheduled_from_template_id;
              if (!templateId) {
                throw new Error('Template id not found');
              }
              return {
                templateId,
                guestName: event.guestName,
                guestEmail: event.guestEmail,
                guestPhone: event.guestPhone,
                sessionId: session?.id,
                guestTimezone: timezone,
                sequenceDay: currentSequenceNumber ?? 0,
              };
            },
            onDone: {
              target: '#booking.notifySuccess',
            },
            onError: {
              target: 'error',
              actions: assign({
                error: ({event}) => event.error,
              }),
            },
            src: 'sendNotification',
          },
        },

        error: {
          on: {
            'notify.submit': {
              target: 'submitting',
            },
          },
        },
      },
    },

    bookingForm: {
      initial: 'form',
      states: {
        form: {
          on: {
            'form.submit': {
              target: 'submitting',
            },
          },
        },
        submitting: {
          invoke: {
            input: ({
              context: {
                eventTemplate,
                timezone,
                sequenceDays,
                session,
                language,
              },
              event,
            }) => {
              assertEvent(event, 'form.submit');

              // Get date and start time from day 0
              const {selectedDate, selectedTimeSlot, timeSlots} =
                sequenceDays?.[0] || {};
              const date = selectedDate;
              const startTime = selectedTimeSlot?.start_time;

              if (!timeSlots) {
                throw new Error('Time slots not found for current day');
              }

              // get formatted date and start time from all days
              const selectedTimeslots = getSelectedTimeslots(sequenceDays);

              const firstAvailableDate = Object.keys(
                timeSlots.free_time_slots || {},
              )[0];
              const firstAvailableTimeList =
                timeSlots.free_time_slots[firstAvailableDate];
              const startAvailableTime = firstAvailableTimeList?.[0].start_time;

              if (eventTemplate?.id && date && startTime) {
                return {
                  templateId: eventTemplate?.id,
                  timezone,
                  date,
                  startTime,
                  guestName: event.guestName,
                  guestEmail: event.guestEmail,
                  guestPhone: event.guestPhone,
                  firstAvailableDatetime: `${firstAvailableDate} ${startAvailableTime}`,
                  sessionId: session?.id,
                  language,
                  additionalQuestionAnswers:
                    event.additionalQuestionAnswers || [],
                  selectedTimeslots,
                };
              }
              throw new Error(
                'Template id, selected date or selected time slot not found',
              );
            },
            onDone: {
              target: '#booking.bookingSuccess',
              actions: assign({
                eventDetails: ({event}) => event.output,
              }),
            },
            onError: {
              target: 'error',
              actions: assign({
                error: ({event}) => event.error,
              }),
            },
            src: 'createEvent',
          },
        },
        error: {
          always: [
            {
              target: 'unavailableTimeSlot',
              guard: 'isTimeSlotUnavailable',
              reenter: true,
            },
            'retrySubmit',
          ],
        },
        retrySubmit: {
          on: {
            'form.submit': {
              target: 'submitting',
              reenter: true,
            },
          },
        },
        unavailableTimeSlot: {
          on: {
            'refetchAvailability.go': {
              target: '#booking.dateTimePicker.availabilityLoading',
              reenter: true,
            },
          },
        },
      },
      on: {
        'bookingForm.back': {
          target: '#booking.dateTimePicker.availabilitySlots',
        },
      },
    },

    rescheduleForm: {
      initial: 'rescheduleReasonForm',
      on: {
        'rescheduleReasonForm.back': {
          target: 'dateTimePicker.availabilitySlots',
          reenter: true,
          actions: assign({
            rescheduleReason: undefined,
          }),
        },
      },
      states: {
        rescheduleReasonForm: {
          on: {
            'rescheduleReason.update': {
              actions: assign({
                rescheduleReason: ({event}) => event.reason,
              }),
            },
            'timeslot.confirm': 'rescheduleFormSubmitting',
          },
        },

        rescheduleFormSubmitting: {
          invoke: {
            input: ({
              context: {
                eventDetails,
                attendee,
                sequenceDays,
                currentSequenceNumber,
                timezone,
                rescheduleReason,
              },
              self,
            }) => {
              const sequenceNumber = currentSequenceNumber ?? 0;
              const selectedDay = sequenceDays?.find(
                (day) => day.sequenceNumber === sequenceNumber,
              );
              const timeSlots = selectedDay?.timeSlots;

              if (!timeSlots) {
                throw new Error('Time slots not found for current day');
              }

              const firstDate = Object.keys(timeSlots.free_time_slots || {})[0];
              const firstDateTimeList = timeSlots.free_time_slots[firstDate];
              const startTime = firstDateTimeList?.[0].start_time;

              const rootMachine = self.system.get('root');
              const rootSnapshot = rootMachine?.getSnapshot();

              // Get values from root context
              const aid = rootSnapshot?.context.aid;

              if ((eventDetails as IEvent)?.id && selectedDay?.selectedDate) {
                return {
                  eventId: (eventDetails as IEvent)?.id,
                  date: selectedDay.selectedDate,
                  startTime: selectedDay.selectedTimeSlot?.start_time || '',
                  timezone: timezone || '',
                  attendee: attendee || '',
                  firstAvailableDatetime: `${firstDate} ${startTime}`,
                  reason: rescheduleReason || '',
                  aid:
                    (eventDetails as IEvent)?.candidates?.[0]?.id || aid || '',
                };
              }
              throw new Error('Event id or selected date not found');
            },
            onDone: {
              target: '#booking.bookingSuccess',
              actions: assign({
                eventDetails: ({event}) => event.output,
              }),
            },
            onError: {
              target: 'error',
              actions: assign({
                error: ({event}) => event.error,
              }),
            },
            src: 'updateEvent',
          },
        },

        error: {
          always: [
            {
              target: 'unavailableTimeSlotForReschedule',
              guard: 'isTimeSlotUnavailable',
            },
            {
              target: 'retryRescheduleFormSubmit',

              actions: assign({
                sequenceDays: ({context}): SequenceDay[] =>
                  updateSequenceDays(
                    context.sequenceDays,
                    context.currentSequenceNumber ?? 0,
                    {
                      selectedTimeSlot: undefined,
                    },
                  ) as SequenceDay[],
              }),

              reenter: true,
            },
          ],
        },

        unavailableTimeSlotForReschedule: {
          on: {
            'refetchAvailabilityForReschedule.go': {
              target: '#booking.dateTimePicker.availabilityLoading',
              reenter: true,
            },
          },
        },

        retryRescheduleFormSubmit: {
          on: {
            retryRescheduleFormSubmitting: 'rescheduleFormSubmitting',
          },
        },
      },
    },
  },
  output: ({context: {eventDetails, finalState, rescheduleReason}}) => ({
    eventDetails,
    finalState,
    rescheduleReason,
  }),
});

export type IBookingMachineSnapshot = SnapshotFrom<typeof bookingMachine>;
